import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { LineLayer } from 'mapbox-gl';
import { Form, Row } from '@components/form/Form/Form';
import { MapMarkerField } from '@components/form/MapMarkerField/MapMarkerField';
import { getFilledFieldsObjOnFormSubmit, isErrorResponse } from '@utils/utils';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSourceById } from '@redux/selectors/selectors';
import { serviceApi } from '@api/serviceApi';
import * as SelectBox from '@components/form/SelectBox/SelectBox';
import { Option } from '@components/form/SelectBox/SelectBox';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import {
    cleanRoadRouteCheckPoints,
    ROAD_ROUTE_CHECKER_MARKER_NAME,
} from '@redux/road-route-check/road-route-check-reducer';
import './road-route-checker.scss';
import { RoadInfrastructureRouteEntity } from '@api/entities/replancity_RoadInfrastructureRoute';
import RoadSegmentCard from './RoadSegmentCard';
import { useToastContext } from '@context/toastContext';
import { useMapMarkersCoordinates } from '@hooks/useMapMarkersCoordinates';
import { usePresetsContext } from '@context/presetsContext';
import { RoadInfrastructureEntityName } from '@api/types/types';
import type * as schema from '@api/schema';
import { EntityServiceName } from '@api/enums/enums';
import { createMapBoxLayer } from '@utils/mapUtils';

const newSourceId = 'route-check-highlight';

const RouteChecker = ({
    entityServiceName,
    formFields,
}: {
    entityServiceName: EntityServiceName;
    formFields?: React.ReactNode;
}) => {
    const entityName = RoadInfrastructureEntityName.RoadSegment;
    const sourceId = useTypedSelector(state => selectMapLayerIdByEntityName(state, entityName));
    const roadSegmentsSource = useTypedSelector(state => selectSourceById(state, sourceId));
    const [loading, setLoading] = useState<boolean>(false);
    const [routeDetails, setRouteDetails] = useState<RoadInfrastructureRouteEntity>({
        id: '',
        distance: 0,
        segments: [],
        straightDistance: 0,
        time: 0,
    });
    const [modes, setModes] = useState<Option[]>([]);
    const formRef = useRef<HTMLFormElement>(null);
    const { selectedPresetId: presetId } = usePresetsContext();
    const { markersCoordinates } = useMapMarkersCoordinates({
        fromPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint,
        toPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint,
    });
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    const { mapboxMapId } = useMap();

    useEffect(() => {
        return () => {
            dispatch(cleanRoadRouteCheckPoints());
        };
    }, []);

    useEffect(() => {
        return () => {
            try {
                const map = mapboxMapId?.getMap();
                if (!map) {
                    return;
                }

                if (map.isStyleLoaded() && map?.getLayer(newSourceId)) {
                    map?.removeLayer(newSourceId);
                }
            } catch (e) {
                console.error(
                    "Replan. Route check: for the moment it's not clear how to fix this issue, therefore it's intentionally wrapped by try catch"
                );
            }
        };
    }, [mapboxMapId]);

    useEffect(() => {
        if (presetId) {
            const abortController = new AbortController();
            void (async function () {
                setLoading(true);

                const resp = await serviceApi.getModes<
                    schema.paths['/services/replancity_RoadInfrastructureService/listAllModes']['get']['parameters']['query']
                >(entityServiceName, { presetId }, abortController.signal);
                if (resp && Array.isArray(resp)) {
                    // if (!isErrorResponse(resp)) {
                    setModes(
                        resp.map(mode => ({
                            value: mode,
                        }))
                    );
                } else {
                    console.error("Replan. listAllModes response doesn't include array of modes");
                }

                setLoading(false);
            })();
        }
    }, [presetId]);

    const onFormSubmit = useCallback(
        async event => {
            const abortController = new AbortController();

            const formDataObj = getFilledFieldsObjOnFormSubmit(event);

            const fromPoint = JSON.parse(formDataObj.fromPoint as string);
            const toPoint = JSON.parse(formDataObj.toPoint as string);

            setLoading(true);

            const resp = await serviceApi.calculateAndGetRouteGeometry(
                entityServiceName,
                {
                    presetId,
                    fromLon: fromPoint.lng,
                    fromLat: fromPoint.lat,
                    toLon: toPoint.lng,
                    toLat: toPoint.lat,
                    mode: formDataObj.mode as string,
                },
                abortController.signal
            );

            if (isErrorResponse(resp)) {
                addToast(`${t('route-check.wrong-point-error-toast')}`, 'error');
            } else {
                const { distance, id, segments, straightDistance, time } = resp;

                const featureStateIds = segments.map(({ featureStateId }) => featureStateId);

                setRouteDetails({ distance, id, segments, straightDistance, time });

                const map = mapboxMapId?.getMap();

                if (map?.getLayer(newSourceId)) {
                    map?.removeLayer(newSourceId);
                }

                const mapBoxLayer = () => {
                    const layer = createMapBoxLayer(roadSegmentsSource);
                    return {
                        ...layer,
                        source: sourceId,
                        id: newSourceId,
                        layerName: newSourceId,
                        layout: {
                            ...layer.layout,
                            visibility: 'visible',
                        },
                        paint: {
                            ...layer.paint,
                            'line-color': 'red',
                        },
                        filter: ['in', ['get', 'featureStateId'], ['literal', featureStateIds]],
                    };
                };

                map?.addLayer(mapBoxLayer() as LineLayer);
            }

            setLoading(false);

            return;
        },
        [entityName, presetId, sourceId, roadSegmentsSource, mapboxMapId]
    );

    return (
        <div className="road-route-checker">
            <div className="road-route-checker__form">
                <div className="road-route-checker__header"></div>
                <LoadingBackdrop isVisible={loading} transparent>
                    <div className="entity-editor__header">
                        <div className="title">{t('route-check.header-title')}</div>
                    </div>
                    {markersCoordinates.fromPoint && markersCoordinates.toPoint ? (
                        <Form
                            name="entityForm"
                            onSubmit={onFormSubmit}
                            submitBtnTitle={t('route-check.form-submit-btn')}
                            ref={formRef}
                            submitBtnTestId="entityFormSubmitBtn"
                            testId="entityForm"
                        >
                            <Row>
                                <MapMarkerField
                                    name={ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint}
                                    defaultCoordinates={markersCoordinates.fromPoint}
                                    fieldGroupClassName="map-marker-form-field"
                                />
                                <MapMarkerField
                                    name={ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint}
                                    defaultCoordinates={markersCoordinates.toPoint}
                                    fieldGroupClassName="map-marker-form-field"
                                />
                            </Row>
                            <Row>
                                <SelectBox.Labeled
                                    label={t('route-check.form-transport-mode')}
                                    name="mode"
                                    options={modes}
                                />
                            </Row>
                        </Form>
                    ) : null}
                </LoadingBackdrop>
            </div>
            <div className="road-route-checker__result">
                {routeDetails.segments.length ? (
                    <>
                        <div className="road-route-checker__distances">
                            Distance: {routeDetails.distance}, straight distance:{' '}
                            {routeDetails.straightDistance}, time: {routeDetails.time}
                        </div>
                        <div className="road-route-checker__segments">
                            {routeDetails.segments.map(segment => (
                                <RoadSegmentCard key={segment.id} {...segment} />
                            ))}
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default RouteChecker;
